/**
 * Utility to sanitize messages by removing emojis and other unwanted characters
 * This prevents AI-generated or unintended emojis from appearing in toast notifications
 */

/**
 * Removes emojis and other Unicode symbols from a string
 * @param text - The text to sanitize
 * @returns The sanitized text without emojis
 */
export function sanitizeMessage(text: string): string {
  if (!text || typeof text !== 'string') {
    return text;
  }

  const sanitized = Array.from(text)
    .filter((char) => {
      const codePoint = char.codePointAt(0);
      if (codePoint === undefined) {
        return true;
      }
      return !isEmojiCodePoint(codePoint);
    })
    .join('');

  // Clean up multiple spaces that might result from emoji removal
  return sanitized.replace(/\s+/g, ' ').trim();
}

/**
 * Sanitizes a message for display in toast notifications
 * Ensures no emojis or unwanted characters appear
 * @param message - The message to sanitize
 * @returns The sanitized message
 */
export function sanitizeToastMessage(message: string | undefined | null): string {
  if (!message) {
    return '';
  }
  return sanitizeMessage(String(message));
}

// -----------------------------
// Emoji detection helpers
// -----------------------------

const EMOJI_RANGES: Array<[number, number]> = [
  [0x1f300, 0x1f5ff],
  [0x1f600, 0x1f64f],
  [0x1f680, 0x1f6ff],
  [0x1f700, 0x1f77f],
  [0x1f780, 0x1f7ff],
  [0x1f800, 0x1f8ff],
  [0x1f900, 0x1f9ff],
  [0x1fa00, 0x1faff],
  [0x1fb00, 0x1fbff],
  [0x1f1e0, 0x1f1ff], // Regional indicator symbols (flags)
  [0x2600, 0x26ff],
  [0x2700, 0x27bf],
  [0x2900, 0x297f], // Arrows often used as emoji
  [0x2b00, 0x2bff],
  [0xfe00, 0xfe0f], // Variation selectors
];

const EMOJI_CODEPOINTS = new Set<number>([
  0x200d, // Zero width joiner
  0x203c,
  0x2049,
  0x2122,
  0x2139,
  0x231a,
  0x231b,
  0x2328,
  0x23cf,
  0x23e9,
  0x23ea,
  0x23eb,
  0x23ec,
  0x23ed,
  0x23ee,
  0x23ef,
  0x23f0,
  0x23f1,
  0x23f2,
  0x23f3,
  0x23f8,
  0x23f9,
  0x23fa,
  0x24c2,
  0x2614,
  0x2615,
  0x2648,
  0x2649,
  0x264a,
  0x264b,
  0x264c,
  0x264d,
  0x264e,
  0x264f,
  0x2650,
  0x2651,
  0x2652,
  0x2653,
  0x267e,
  0x267f,
  0x2692,
  0x2693,
  0x2694,
  0x2695,
  0x2696,
  0x2697,
  0x26a0,
  0x26a1,
  0x26aa,
  0x26ab,
  0x26bd,
  0x26be,
  0x2702,
  0x2705,
  0x2708,
  0x2709,
  0x270a,
  0x270b,
  0x270c,
  0x270d,
  0x270f,
  0x2712,
  0x2714,
  0x2716,
  0x2721,
  0x2728,
  0x2733,
  0x2734,
  0x2744,
  0x2747,
  0x274c,
  0x274e,
  0x2753,
  0x2754,
  0x2755,
  0x2757,
  0x2763,
  0x2764,
  0x2795,
  0x2796,
  0x2797,
  0x27a1,
  0x27b0,
  0x27bf,
  0x2934,
  0x2935,
  0x2b05,
  0x2b06,
  0x2b07,
  0x2b1b,
  0x2b1c,
  0x2b50,
  0x2b55,
  0x3030,
  0x303d,
  0x3297,
  0x3299,
]);

function isEmojiCodePoint(codePoint: number): boolean {
  if (EMOJI_CODEPOINTS.has(codePoint)) {
    return true;
  }

  return EMOJI_RANGES.some(([start, end]) => codePoint >= start && codePoint <= end);
}
